import { graphHooks } from '@tunasong/graph-lib/react'
import { profilesApi, useCurrentUser } from '@tunasong/redux'
import type { BandSpace, Persisted, Profile, Rider } from '@tunasong/schemas'
import { useUserConfigSave } from '@tunasong/ui-lib'
import { bandMemberIds, getReadiness } from '../readiness.js'
import { setNotPerforming } from './opt-out.js'

/**
 * Readiness for a band's latest rider, from live data: bandmates' public profiles and the band-scoped opt-outs. The
 * viewer's own setup and opt-out are read from their config as well, so a setup saved in the dialog, or a toggled
 * opt-out, shows at once instead of after the profile list refetches.
 */
export const useBandReadiness = (band: Persisted<BandSpace>, riders: Persisted<Rider>[]) => {
  const { userId: viewerId } = useCurrentUser()
  const memberIds = bandMemberIds(band)
  const {
    profiles,
    isSuccess: profilesLoaded,
    isError: profilesFailed,
  } = graphHooks.useProfiles({ userIds: memberIds, includeAssistant: false })
  const { currentData: bandParts, isError: partsFailed } = profilesApi.useLoadBandPartsQuery({ bandId: band.id })
  const [performance, savePerformance] = useUserConfigSave('performance')
  const [viewerGear] = useUserConfigSave('gear')

  const viewerOptedOut = Boolean(performance?.notPerformingBands?.includes(band.id))
  const serverOptOuts = partsFailed || !bandParts ? undefined : (bandParts.notPerforming ?? [])
  const notPerforming = serverOptOuts
    ? [...serverOptOuts.filter(id => id !== viewerId), ...(viewerOptedOut && viewerId ? [viewerId] : [])]
    : undefined

  const loadedProfiles =
    profilesLoaded && !profilesFailed
      ? (profiles as Profile[]).map(profile =>
          profile.userId === viewerId && viewerGear ? ({ ...profile, gear: viewerGear } as Profile) : profile
        )
      : undefined
  const readiness = getReadiness({ memberIds, profiles: loadedProfiles, notPerforming, viewerId, riders })

  const setPerforming = (performs: boolean) => savePerformance(setNotPerforming(performance, band.id, !performs))

  return { readiness, profiles: loadedProfiles ?? [], viewerId, setPerforming }
}
